import React, { useEffect, useRef, useState } from "react";
import {
    Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton,
    DrawerHeader, DrawerBody, Box, Flex, Text, Input, InputGroup,
    InputLeftElement, Checkbox, Badge, Spinner, Tooltip,
    Collapse, useDisclosure, IconButton, Tabs, TabList, TabPanels,
    Tab, TabPanel, Button, FormControl, FormLabel, Switch, HStack,
    VStack, AlertDialog, AlertDialogBody, AlertDialogFooter,
    AlertDialogHeader, AlertDialogContent, AlertDialogOverlay,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
    ModalFooter, ModalCloseButton, Textarea,
    Table,
    Thead,
    Tr,
    Th,
    Tbody,
    Td,
} from "@chakra-ui/react";
import {
    Search2Icon, ChevronDownIcon, ChevronUpIcon,
    AddIcon, EditIcon, DeleteIcon,
} from "@chakra-ui/icons";
import { CustomToast } from "../../../../components";
import { useFetch } from "../../../../hooks/useFetch";

// ── Types ──────────────────────────────────────────────────────────────────────
interface Document {
    id: number;
    document_Name: string;
    description: string;
    can_Accept_Duplicate: boolean;
    branch_Wise: boolean;
    isMapped: boolean;
}

interface Category {
    category_Id: number;
    category_Name: string;
    isAllSelected: boolean;
    documents: Document[];
}

interface AdditionalDoc {
    id: number;
    document_Name: string;
    description: string;
    can_Accept_Duplicate: boolean;
    branch_Wise: boolean;
    can_Expire: boolean;
}

// interface Props {
//     isOpen: boolean;
//     onClose: () => void;
//     company: { id: number; company: string } | null;
// }

interface Props {
    isOpen: boolean;
    onClose: () => void;
    company?: { id: number; company: string; } | null;
    payrollCompany?: { id: number; payrollCompanyName: string; } | null;
    type: "COMPANY" | "PAYROLL";
}

const EMPTY_ADD_FORM = {
    id: null as number | null,
    document_Name: "",
    description: "",
    can_Accept_Duplicate: false,
    branch_Wise: false,
    can_Expire: false,
};

// ── Category Row (standard docs) ──────────────────────────────────────────────
const CategoryRow = ({
    category, onDocumentToggle, onCategoryToggle, toggling,
}: {
    category: Category;
    onDocumentToggle: (docId: number, checked: boolean) => void;
    onCategoryToggle: (category: Category, checked: boolean) => void;
    toggling: Set<number>;
}) => {
    const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
    const mappedCount = category.documents.filter(d => d.isMapped).length;
    const total = category.documents.length;

    return (
        <Box mb={3} border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden" bg="white" shadow="sm">
            <Flex align="center" justify="space-between" px={4} py={3} bg={mappedCount > 0 ? "green.50" : "gray.50"} borderBottom={isOpen ? "1px solid" : "none"} borderColor="gray.200" cursor="pointer" onClick={onToggle} _hover={{ bg: mappedCount > 0 ? "green.100" : "gray.100" }} transition="background 0.15s" >
                <Flex align="center" gap={3}>
                    <Checkbox
                        isChecked={category.isAllSelected}
                        isIndeterminate={mappedCount > 0 && mappedCount < total}
                        colorScheme="green"
                        onClick={e => e.stopPropagation()}
                        onChange={e => onCategoryToggle(category, e.target.checked)}
                    >
                        <Text fontSize="xs" fontWeight="600" color="gray.600">Select All</Text>
                    </Checkbox>
                    <Text fontWeight="700" fontSize="sm" color="gray.700">{category.category_Name}</Text>
                    <Badge colorScheme={mappedCount === total ? "green" : mappedCount > 0 ? "orange" : "gray"} variant="subtle" fontSize="xs" borderRadius="full" px={2} >
                        {mappedCount}/{total}
                    </Badge>
                    <IconButton aria-label="toggle" icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />} size="xs" variant="ghost" colorScheme="gray" onClick={e => { e.stopPropagation(); onToggle(); }} />
                </Flex>

            </Flex>
            <Collapse in={isOpen} animateOpacity>
                <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                        <Thead bg="gray.50">
                            <Tr>
                                <Th width="60px">Assign</Th>
                                <Th>Document Name</Th>
                                <Th>Description</Th>
                                <Th textAlign="center">Duplicate Allowed</Th>
                                <Th textAlign="center">Branch Wise</Th>
                            </Tr>
                        </Thead>

                        <Tbody>

                            {category.documents.map((doc) => (
                                <Tr key={doc.id} bg={doc.isMapped ? "green.50" : "white"} _hover={{ bg: doc.isMapped ? "green.100" : "gray.50" }} transition="0.2s" >
                                    <Td>
                                        <Checkbox isChecked={doc.isMapped} colorScheme="green" onChange={(e) => onDocumentToggle(doc.id, e.target.checked)} size="lg" />
                                    </Td>
                                    <Td>
                                        <Text fontWeight="600" fontSize="sm" color="gray.800" > {doc.document_Name} </Text>
                                    </Td>
                                    <Td>
                                        <Text fontSize="xs" color="gray.500" lineHeight="1.5" > {doc.description || "-"} </Text>
                                    </Td>
                                    <Td textAlign="center">
                                        <Badge colorScheme={doc.can_Accept_Duplicate ? "green" : "red"} variant="subtle" px={2} py={1} borderRadius="md" > {doc.can_Accept_Duplicate ? "Yes" : "No"} </Badge>
                                    </Td>
                                    <Td textAlign="center">
                                        <Badge colorScheme={doc.branch_Wise ? "purple" : "gray"} variant="subtle" px={2} py={1} borderRadius="md" > {doc.branch_Wise ? "Yes" : "No"} </Badge>
                                    </Td>
                                </Tr>

                            ))}
                        </Tbody>
                    </Table>
                </Box>
            </Collapse>
        </Box>
    );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const CompanyDocumentDrawer: React.FC<Props> = ({ isOpen, onClose, company, payrollCompany, type }) => {
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);

    // Standard docs state
    const [categories, setCategories] = useState<Category[]>([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [toggling, setToggling] = useState<Set<number>>(new Set());
    const [search, setSearch] = useState("");
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [additionalDocs, setAdditionalDocs] = useState<AdditionalDoc[]>([]);
    const [loadingAdditional, setLoadingAdditional] = useState(false);
    const [addSearch, setAddSearch] = useState("");
    const addSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [addForm, setAddForm] = useState(EMPTY_ADD_FORM);
    const [savingAdd, setSavingAdd] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const modalDisc = useDisclosure();
    const deleteDialog = useDisclosure();
    const cancelRef = useRef<HTMLButtonElement>(null);
    const [deleteTarget, setDeleteTarget] = useState<AdditionalDoc | null>(null);

    // ── Stats ──────────────────────────────────────────────────────────────────
    const totalDocs = categories.reduce((s, c) => s + c.documents.length, 0);
    const mappedDocs = categories.reduce((s, c) => s + c.documents.filter(d => d.isMapped).length, 0);
    const pct = totalDocs > 0 ? Math.round((mappedDocs / totalDocs) * 100) : 0;

    // ── Load on open ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (isOpen && (company || payrollCompany)) {
            setSearch(""); setAddSearch("");
            loadDocuments(""); loadAdditional("");
        }
    }, [isOpen, company, payrollCompany]);

    // Standard doc search debounce
    useEffect(() => {
        if (!company || !payrollCompany) return;
        clearTimeout(searchTimer.current ?? undefined);
        searchTimer.current = setTimeout(() => loadDocuments(search), 400);
        return () => clearTimeout(searchTimer.current ?? undefined);
    }, [search]);

    // Additional doc search debounce
    useEffect(() => {
        if (!company || !payrollCompany) return;
        clearTimeout(addSearchTimer.current ?? undefined);
        addSearchTimer.current = setTimeout(() => loadAdditional(addSearch), 400);
        return () => clearTimeout(addSearchTimer.current ?? undefined);
    }, [addSearch]);

    // ── API calls ──────────────────────────────────────────────────────────────
  

    const loadDocuments = async (q = "") => {
        const id = company?.id || payrollCompany?.id;

        if (!id) return;
        setLoadingDocs(true);


        const url = type === "COMPANY" ? `Hrlense_Company/Documents/mapping?companyId=${id}&search=${q}` : `Hrlense_Company/Documents/mapping?payrollCompanyId=${id}&search=${q}`;
        const res = await fetchApi<Category[]>(url, "GET");
        if (res) setCategories(res);
        setLoadingDocs(false);

    };

    const loadAdditional = async (q = "") => {
        const id = company?.id || payrollCompany?.id;
        if (!id) return;

        setLoadingAdditional(true);
        const url = type === "COMPANY"
            ? `Hrlense_Company/AdditionalDocument?companyId=${id}&search=${q}`
            : `Hrlense_Company/AdditionalDocument?payrollCompanyId=${id}&search=${q}`;

        const res = await fetchApi<AdditionalDoc[]>(url, "GET");
        if (res) setAdditionalDocs(res);
        setLoadingAdditional(false);

    };

    // ── Standard doc toggle ────────────────────────────────────────────────────
    const handleDocumentToggle = async (docId: number, checked: boolean) => {
        setCategories(prev => prev.map(cat => ({
            ...cat,
            documents: cat.documents.map(d => d.id === docId ? { ...d, isMapped: checked } : d),
            isAllSelected: cat.documents.every(d => d.id === docId ? checked : d.isMapped),
        })));
        setToggling(prev => new Set(prev).add(docId));
        try {
            await fetchApi("Hrlense_Company/toggle", "POST", {
                companyId: type === "COMPANY" ? company!.id : null,
                payrollCompanyId: type === "PAYROLL" ? payrollCompany!.id : null,
                documentId: docId,
                isMapped: checked,
            });
        } catch {
            setCategories(prev => prev.map(cat => ({
                ...cat,
                documents: cat.documents.map(d => d.id === docId ? { ...d, isMapped: !checked } : d),
                isAllSelected: cat.documents.every(d => d.isMapped),
            })));
        } finally {
            setToggling(prev => { const s = new Set(prev); s.delete(docId); return s; });
        }
    };

    const handleCategoryToggle = async (category: Category, checked: boolean) => {
        const docIds = category.documents.map(d => d.id);
        setCategories(prev => prev.map(cat =>
            cat.category_Id === category.category_Id
                ? { ...cat, isAllSelected: checked, documents: cat.documents.map(d => ({ ...d, isMapped: checked })) }
                : cat
        ));
        docIds.forEach(id => setToggling(prev => new Set(prev).add(id)));
        await Promise.all(category.documents.map(doc =>
            // fetchApi("Hrlense_Company/toggle", "POST", { companyId: company!.id, documentId: doc.id, isMapped: checked, })

            fetchApi("Hrlense_Company/toggle", "POST", {
                companyId: type === "COMPANY" ? company!.id : null,
                payrollCompanyId: type === "PAYROLL" ? payrollCompany!.id : null,
                documentId: doc.id,
                isMapped: checked,
            })

        ));
        docIds.forEach(id => setToggling(prev => { const s = new Set(prev); s.delete(id); return s; }));
    };

    // ── Additional doc CRUD ────────────────────────────────────────────────────
    const openAddForm = () => { setAddForm(EMPTY_ADD_FORM); modalDisc.onOpen(); };

    const openEditForm = (doc: AdditionalDoc) => {
        setAddForm({
            id: doc.id,
            document_Name: doc.document_Name,
            description: doc.description ?? "",
            can_Accept_Duplicate: doc.can_Accept_Duplicate,
            branch_Wise: doc.branch_Wise,
            can_Expire: doc.can_Expire
        });
        modalDisc.onOpen();
    };

    const handleSaveAdditional = async () => {
        if (!addForm.document_Name.trim()) {
            addToast({ message: "Document name is required", status: "warning" });
            return;
        }
        setSavingAdd(true);
        try {
            const payload = {
                document_Name: addForm.document_Name.trim(),
                description: addForm.description.trim() || null,
                can_Accept_Duplicate: addForm.can_Accept_Duplicate,
                branch_Wise: addForm.branch_Wise,
                can_Expire: addForm.can_Expire,
            };

            const ownerId = type === "COMPANY" ? company!.id : payrollCompany!.id;


            if (addForm.id) {
                const res = await fetchApi<AdditionalDoc>(`Hrlense_Company/AdditionalDocument/${addForm.id}`, "PUT", payload);
                if (res) {
                    setAdditionalDocs(prev => prev.map(d => d.id === addForm.id ? res : d));
                    addToast({ message: "Document updated successfully", status: "success" });
                    modalDisc.onClose();
                }
            } else {
                const url = type === "COMPANY"
                    ? `Hrlense_Company/AdditionalDocument?companyId=${ownerId}`
                    : `Hrlense_Company/AdditionalDocument?payrollCompanyId=${ownerId}`;
                const res = await fetchApi<AdditionalDoc>(url, "POST", payload); if (res) {
                    setAdditionalDocs(prev => [...prev, res]);
                    addToast({ message: "Document added successfully", status: "success" });
                    modalDisc.onClose();
                }
            }
        } finally { setSavingAdd(false); }
    };

    const confirmDelete = (doc: AdditionalDoc) => { setDeleteTarget(doc); deleteDialog.onOpen(); };

    const handleDeleteAdditional = async () => {
        if (!deleteTarget) return;
        setDeletingId(deleteTarget.id);
        try {
            const res = await fetchApi(`Hrlense_Company/AdditionalDocument/${deleteTarget.id}`, "DELETE");
            if (res !== null && res !== undefined) {
                setAdditionalDocs(prev => prev.filter(d => d.id !== deleteTarget.id));
                addToast({ message: "Document deleted", status: "info" });
                deleteDialog.onClose();
            }
        } finally { setDeletingId(null); }
    };

    const visible = categories.filter(c => c.documents.length > 0);

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <>
            <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
                <DrawerOverlay backdropFilter="blur(2px)" />
                <DrawerContent>
                    <DrawerCloseButton />

                    {/* Header */}
                    <DrawerHeader borderBottomWidth="1px" pb={3}>
                        <Text fontSize="lg" fontWeight="700" color="gray.800">Assign Documents</Text>
                        <Text fontSize="sm" color="gray.500" fontWeight="400">
                            {type === "COMPANY" ? company?.company : payrollCompany?.payrollCompanyName}
                        </Text>
                        {/* Progress bar (standard docs only) */}
                        {!loadingDocs && totalDocs > 0 && (
                            <Box mt={3} mb={1}>
                                <Flex justify="space-between" mb={1}>
                                    <Text fontSize="xs" color="gray.500">{mappedDocs} of {totalDocs} standard documents assigned</Text>
                                    <Text fontSize="xs" fontWeight="700" color={pct === 100 ? "green.600" : "blue.600"}>{pct}%</Text>
                                </Flex>
                                <Box h="4px" bg="gray.100" borderRadius="full" overflow="hidden">
                                    <Box h="full" w={`${pct}%`} bg={pct === 100 ? "green.400" : "blue.400"} borderRadius="full" transition="width 0.3s ease" />
                                </Box>
                            </Box>
                        )}
                    </DrawerHeader>

                    <DrawerBody pt={0} pb={6}>
                        <Tabs variant="enclosed" colorScheme="green" pt={4}>
                            <TabList>
                                <Tab fontWeight="600" fontSize="sm">
                                    Standard Documents
                                    <Badge ml={2} colorScheme="green" variant="subtle" borderRadius="full">{mappedDocs}/{totalDocs}</Badge>
                                </Tab>
                                <Tab fontWeight="600" fontSize="sm">
                                    Additional Documents
                                    <Badge ml={2} colorScheme="blue" variant="subtle" borderRadius="full">{additionalDocs.length}</Badge>
                                </Tab>
                            </TabList>

                            <TabPanels>
                                {/* ── Tab 1: Standard Documents ─────────────────────── */}
                                <TabPanel px={0} pt={4}>
                                    <InputGroup mb={4} size="sm">
                                        <InputLeftElement pointerEvents="none">
                                            <Search2Icon color="gray.400" />
                                        </InputLeftElement>
                                        <Input placeholder="Search documents or categories…" value={search} onChange={e => setSearch(e.target.value)} bg="gray.50" borderRadius="md" />
                                    </InputGroup>

                                    {loadingDocs ? (
                                        <Flex justify="center" align="center" h="200px"><Spinner size="xl" color="green.500" thickness="3px" /></Flex>
                                    ) : visible.length === 0 ? (
                                        <Flex direction="column" align="center" justify="center" h="200px" gap={2}>
                                            <Text fontSize="lg" color="gray.400" fontWeight="600">No documents found</Text>
                                            {search && <Text fontSize="sm" color="gray.400">Try clearing your search</Text>}
                                        </Flex>
                                    ) : (
                                        visible.map(cat => (
                                            <CategoryRow key={cat.category_Id} category={cat} onDocumentToggle={handleDocumentToggle} onCategoryToggle={handleCategoryToggle} toggling={toggling} />
                                        ))
                                    )}
                                </TabPanel>

                                {/* ── Tab 2: Additional Documents ───────────────────── */}
                                <TabPanel px={0} pt={4}>
                                    <Flex justify="space-between" align="center" mb={4} gap={3}>
                                        <InputGroup size="sm" flex={1}>
                                            <InputLeftElement pointerEvents="none"><Search2Icon color="gray.400" /></InputLeftElement>
                                            <Input placeholder="Search additional documents…" value={addSearch} onChange={e => setAddSearch(e.target.value)} bg="gray.50" borderRadius="md" />
                                        </InputGroup>
                                        <Button size="sm" colorScheme="green" leftIcon={<AddIcon />} onClick={openAddForm} flexShrink={0} >Add Document</Button>
                                    </Flex>

                                    {loadingAdditional ? (
                                        <Flex justify="center" align="center" h="200px"><Spinner size="xl" color="blue.500" thickness="3px" /></Flex>
                                    ) : additionalDocs.length === 0 ? (
                                        <Flex direction="column" align="center" justify="center" h="200px" bg="white" borderRadius="xl" border="1px dashed" borderColor="gray.200" gap={3} >
                                            <Text fontSize="lg" color="gray.400" fontWeight="600">No additional documents</Text>
                                            <Text fontSize="sm" color="gray.400">Add company-specific documents here</Text>
                                            <Button size="sm" colorScheme="green" leftIcon={<AddIcon />} onClick={openAddForm}>Add First Document</Button>
                                        </Flex>
                                    ) : (
                                        <Box overflowX="auto">
                                            <Table size="sm" variant="simple">
                                                <Thead bg="gray.50">
                                                    <Tr>
                                                        <Th>Document Name</Th>
                                                        <Th>Description</Th>
                                                        <Th textAlign="center">Duplicate Allowed</Th>
                                                        <Th textAlign="center">Branch Wise</Th>
                                                        <Th textAlign="center">Can Expire</Th>
                                                        <Th textAlign="center">Actions</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>

                                                    {additionalDocs.map(doc => (
                                                        <Tr key={doc.id} _hover={{ bg: "blue.50" }} >
                                                            <Td>
                                                                <Flex align="center" gap={2} >
                                                                    <Text fontWeight="600" fontSize="sm" color="gray.800" > {doc.document_Name} </Text>
                                                                    <Badge colorScheme="orange" variant="subtle" fontSize="9px" >CUSTOM</Badge>

                                                                </Flex>
                                                            </Td>
                                                            <Td>
                                                                <Text fontSize="xs" color="gray.500" > {doc.description || "-"}</Text>
                                                            </Td>

                                                            <Td textAlign="center">
                                                                <Badge colorScheme={doc.can_Accept_Duplicate ? "green" : "red"} variant="subtle" >{doc.can_Accept_Duplicate ? "Yes" : "No"} </Badge>
                                                            </Td>

                                                            <Td textAlign="center">
                                                                <Badge colorScheme={doc.branch_Wise ? "purple" : "gray"} variant="subtle" > {doc.branch_Wise ? "Yes" : "No"} </Badge>
                                                            </Td>

                                                            <Td textAlign="center">
                                                                <Badge colorScheme={doc.can_Expire ? "orange" : "gray"} variant="subtle" >{doc.can_Expire ? "Yes" : "No"} </Badge>
                                                            </Td>

                                                            <Td textAlign="center">
                                                                <HStack justify="center" spacing={1} >
                                                                    <Tooltip label="Edit">
                                                                        <IconButton icon={<EditIcon />} size="xs" variant="ghost" colorScheme="blue" aria-label="Edit" onClick={() => openEditForm(doc)} />
                                                                    </Tooltip>
                                                                    <Tooltip label="Delete">
                                                                        <IconButton icon={<DeleteIcon />} size="xs" variant="ghost" colorScheme="red" aria-label="Delete" isLoading={deletingId === doc.id} onClick={() => confirmDelete(doc)} />
                                                                    </Tooltip>
                                                                </HStack>
                                                            </Td>
                                                        </Tr>
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </Box>

                                    )}
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            {/* ── Add / Edit Additional Document Modal ──────────────────────────── */}
            <Modal isOpen={modalDisc.isOpen} onClose={modalDisc.onClose} size="md">
                <ModalOverlay backdropFilter="blur(2px)" />
                <ModalContent>
                    <ModalHeader borderBottom="1px solid" borderColor="gray.100" pb={3}>
                        <Text fontSize="md" fontWeight="700">{addForm.id ? "Edit Document" : "Add Additional Document"}</Text>
                        <Text fontSize="xs" color="gray.400" fontWeight="400" mt={0.5}>For {company?.company} only</Text>
                    </ModalHeader>
                    <ModalCloseButton />

                    <ModalBody py={5}>
                        <VStack spacing={4} align="stretch">
                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="600">Document Name</FormLabel>
                                <Input placeholder="e.g. GST Certificate" value={addForm.document_Name} onChange={e => setAddForm(f => ({ ...f, document_Name: e.target.value }))} bg="gray.50" />
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="600">Description</FormLabel>
                                <Textarea placeholder="Optional description…" value={addForm.description} onChange={e => setAddForm(f => ({ ...f, description: e.target.value }))} rows={3} bg="gray.50" fontSize="sm" />
                            </FormControl>

                            <HStack spacing={6}>
                                <FormControl display="flex" alignItems="center" gap={3}>
                                    <Switch colorScheme="blue" isChecked={addForm.can_Accept_Duplicate} onChange={e => setAddForm(f => ({ ...f, can_Accept_Duplicate: e.target.checked }))} />
                                    <FormLabel fontSize="sm" fontWeight="600" mb={0}>Can Accept Duplicates</FormLabel>
                                </FormControl>

                                <FormControl display="flex" alignItems="center" gap={3}>
                                    <Switch colorScheme="purple" isChecked={addForm.branch_Wise} onChange={e => setAddForm(f => ({ ...f, branch_Wise: e.target.checked }))} />
                                    <FormLabel fontSize="sm" fontWeight="600" mb={0}>Branch Wise</FormLabel>
                                </FormControl>
                                <FormControl display="flex" alignItems="center" gap={3}>
                                    <Switch colorScheme="green" isChecked={addForm.can_Expire} onChange={e => setAddForm(f => ({ ...f, can_Expire: e.target.checked }))} />
                                    <FormLabel fontSize="sm" fontWeight="600" mb={0}>Can Expire</FormLabel>
                                </FormControl>
                            </HStack>
                        </VStack>
                    </ModalBody>

                    <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
                        <Button variant="ghost" onClick={modalDisc.onClose} isDisabled={savingAdd}>Cancel</Button>
                        <Button colorScheme="blue" onClick={handleSaveAdditional} isLoading={savingAdd} loadingText="Saving…" >{addForm.id ? "Update" : "Add Document"}</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* ── Delete Confirmation ───────────────────────────────────────────── */}
            <AlertDialog isOpen={deleteDialog.isOpen} leastDestructiveRef={cancelRef} onClose={deleteDialog.onClose}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontWeight="700">Delete Document</AlertDialogHeader>
                        <AlertDialogBody>
                            Are you sure you want to delete <strong>{deleteTarget?.document_Name}</strong>? This cannot be undone.
                        </AlertDialogBody>
                        <AlertDialogFooter gap={3}>
                            <Button ref={cancelRef} onClick={deleteDialog.onClose}>Cancel</Button>
                            <Button colorScheme="red" onClick={handleDeleteAdditional} isLoading={deletingId !== null}>
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default CompanyDocumentDrawer;